import { useCallback, useEffect, useMemo, useState } from "react";
import { getQuizTopicProgress } from "../services/quizRuntimeService";

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const buildFallbackProgress = (topic) => ({
  topic_id: topic?._id || topic?.id || topic?.topic_id || "",
  topic_name: topic?.name || topic?.title || "Topico",
  progress_percentage: 0,
  average_score: 0,
  accuracy_percentage: 0,
  correct_answers: 0,
  incorrect_answers: 0,
  total_questions_answered: 0,
  total_questions: toNumber(topic?.total_questions, 0),
  status: "not_started",
});

const normalizeProgressResponse = (topic, response) => {
  const progress = response?.progress || {};
  const topicInfo = response?.topic || {};
  const totalQuestions = toNumber(progress?.total_questions ?? topic?.total_questions, 0);
  const correctAnswers = toNumber(progress?.correct, 0);

  return {
    topic_id: topic?._id || topic?.id || topic?.topic_id || topicInfo?.id || "",
    topic_name: topicInfo?.name || topic?.name || topic?.title || "Topico",
    progress_percentage: totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0,
    average_score: toNumber(progress?.score, 0),
    accuracy_percentage: toNumber(progress?.accuracy, 0),
    correct_answers: correctAnswers,
    incorrect_answers: toNumber(progress?.incorrect, 0),
    total_questions_answered: toNumber(progress?.answered, 0),
    total_questions: totalQuestions,
    status: response?.status || "not_started",
  };
};

export const useQuizTopicProgress = ({ topics = [], userId, headers, enabled = true } = {}) => {
  const [topicsProgress, setTopicsProgress] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);

  const topicIds = useMemo(
    () => topics.map((topic) => topic?._id || topic?.id || topic?.topic_id).filter(Boolean),
    [topics],
  );

  const loadProgress = useCallback(async () => {
    if (!enabled) {
      setTopicsProgress([]);
      setLoading(false);
      setError(null);
      return;
    }

    if (topicIds.length === 0) {
      setTopicsProgress([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const results = await Promise.allSettled(
      topics.map(async (topic) => {
        try {
          const response = await getQuizTopicProgress({
            topicId: topic?._id || topic?.id || topic?.topic_id,
            userId,
            headers,
          });
          return normalizeProgressResponse(topic, response);
        } catch (err) {
          return buildFallbackProgress(topic);
        }
      }),
    );

    const normalized = results.map((result, index) =>
      result.status === "fulfilled" ? result.value : buildFallbackProgress(topics[index]),
    );

    setTopicsProgress(normalized);
    setLoading(false);
  }, [enabled, headers, topicIds.length, topics, userId]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const progressById = useMemo(
    () =>
      topicsProgress.reduce((acc, topic) => {
        if (topic?.topic_id) {
          acc[topic.topic_id] = topic;
        }
        return acc;
      }, {}),
    [topicsProgress],
  );

  return {
    topicsProgress,
    progressById,
    loading,
    error,
    reload: loadProgress,
  };
};
